// React / Next
import { useEffect, useRef, useState, useCallback } from 'react';

// Styles
import styles from './DigitalRainVector.module.css';

// Types
interface DigitalRainVectorProps {
  className?: string;
  width?: number;
  height?: number;
}

// Animation duration in ms (base duration + max delay for all paths)
const ANIMATION_DURATION = 500 + 166 * 10; // 0.5s base + ~166 paths * 0.01s delay

/**
 * DigitalRainVector - Animated SVG with falling rain effect on hover
 * Paths animate from top to bottom with staggered delays based on Y position
 */
export const DigitalRainVector = ({
  className = '',
  width = 496,
  height = 455,
}: DigitalRainVectorProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const paths = Array.from(svg.querySelectorAll('g path')) as SVGPathElement[];

    // Calculate positions and sort by Y coordinate
    const pathsWithPos = paths.map((path) => {
      const box = path.getBBox();
      return { element: path, val: box.y };
    });

    pathsWithPos.sort((a, b) => a.val - b.val);

    // Assign animation delay index based on sorted position
    pathsWithPos.forEach((item, index) => {
      item.element.style.setProperty('--i', String(index));
    });
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (isAnimating) return;

    setIsAnimating(true);
    timeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
    }, ANIMATION_DURATION);
  }, [isAnimating]);

  return (
    <svg
      ref={svgRef}
      className={`${styles.svg} ${isAnimating ? styles.animating : ''} ${className}`}
      width={width}
      height={height}
      viewBox="0 0 496 455"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Digital rain animated vector"
      role="img"
      onMouseEnter={handleMouseEnter}
    >
      <rect width="496" height="455" fill="#D6F4FF" />
      <g clipPath="url(#digitalRainClip)">
        <path d="M182.697 62H117V72.8306H182.697V62Z" fill="#022E40" />
        <path d="M249.129 64.0004H117V72.8306H249.129V64.0004Z" fill="#022E40" />
        <path d="M312.709 68.001H117V72.8306H312.709V68.001Z" fill="#022E40" />
        <path d="M182.697 73.7496H117V84.5802H182.697V73.7496Z" fill="#022E40" />
        <path d="M249.129 75.741H117V84.5712H249.129V75.741Z" fill="#022E40" />
        <path d="M377.531 79.7416H117V84.5712H377.531V79.7416Z" fill="#022E40" />
        <path d="M377.392 66.6224H312.202V72.8216H377.392V66.6224Z" fill="#022E40" />
        <path d="M182.697 85.6796H117V96.5102H182.697V85.6796Z" fill="#022E40" />
        <path d="M249.129 87.6799H117V96.5102H249.129V87.6799Z" fill="#022E40" />
        <path d="M312.709 91.6805H117V96.5102H312.709V91.6805Z" fill="#022E40" />
        <path d="M377.392 90.3109H312.202V96.5101H377.392V90.3109Z" fill="#022E40" />
        <path d="M182.697 98.0059H117V108.836H182.697V98.0059Z" fill="#022E40" />
        <path d="M249.129 100.006H117V108.836H249.129V100.006Z" fill="#022E40" />
        <path d="M312.709 104.007H117V108.836H312.709V104.007Z" fill="#022E40" />
        <path d="M377.392 102.637H312.202V108.836H377.392V102.637Z" fill="#022E40" />
        <path d="M182.697 109.755H117V120.586H182.697V109.755Z" fill="#022E40" />
        <path d="M249.129 113.45H117V120.577H249.129V113.45Z" fill="#022E40" />
        <path d="M312.709 115.748H117V120.577H312.709V115.748Z" fill="#022E40" />
        <path d="M377.392 114.378H312.202V120.577H377.392V114.378Z" fill="#022E40" />
        <path d="M182.697 121.496H117V132.327H182.697V121.496Z" fill="#022E40" />
        <path d="M249.129 125.47H117V132.318H249.129V125.47Z" fill="#022E40" />
        <path d="M312.709 127.488H117V132.318H312.709V127.488Z" fill="#022E40" />
        <path d="M377.392 126.119H312.202V132.318H377.392V126.119Z" fill="#022E40" />
        <path d="M182.697 133.624H117V144.455H182.697V133.624Z" fill="#022E40" />
        <path d="M249.129 137.607H117V144.455H249.129V137.607Z" fill="#022E40" />
        <path d="M312.709 139.625H117V144.455H312.709V139.625Z" fill="#022E40" />
        <path d="M377.392 138.256H312.202V144.455H377.392V138.256Z" fill="#022E40" />
        <path d="M182.697 145.563H117V156.394H182.697V145.563Z" fill="#022E40" />
        <path d="M249.129 149.537H117V156.385H249.129V149.537Z" fill="#022E40" />
        <path d="M312.709 151.564H117V156.394H312.709V151.564Z" fill="#022E40" />
        <path d="M377.392 150.195H312.202V156.394H377.392V150.195Z" fill="#022E40" />
        <path d="M182.697 170.423H117V179.884H182.697V170.423Z" fill="#022E40" />
        <path d="M378 174.946H245.871V179.875H378V174.946Z" fill="#022E40" />
        <path d="M312.709 176.271H117V179.875H312.709V176.271Z" fill="#022E40" />
        <path d="M377.392 173.676H312.202V179.875H377.392V173.676Z" fill="#022E40" />
        <path d="M182.697 183.335H117V191.03H182.697V183.335Z" fill="#022E40" />
        <path d="M378 186.101H245.871V191.03H378V186.101Z" fill="#022E40" />
        <path d="M312.709 187.426H117V191.03H312.709V187.426Z" fill="#022E40" />
        <path d="M377.392 183.461H312.202V191.03H377.392V183.461Z" fill="#022E40" />
        <path d="M182.697 195.472H117V203.167H182.697V195.472Z" fill="#022E40" />
        <path d="M378 198.239H245.871V203.167H378V198.239Z" fill="#022E40" />
        <path d="M312.709 199.554H117V203.158H312.709V199.554Z" fill="#022E40" />
        <path d="M377.392 195.598H312.202V203.167H377.392V195.598Z" fill="#022E40" />
        <path d="M182.697 207.213H117V214.908H182.697V207.213Z" fill="#022E40" />
        <path d="M378 209.979H245.871V214.908H378V209.979Z" fill="#022E40" />
        <path d="M312.709 211.304H117V214.908H312.709V211.304Z" fill="#022E40" />
        <path d="M377.392 207.339H312.202V214.908H377.392V207.339Z" fill="#022E40" />
        <path d="M182.697 219.539H117V227.234H182.697V219.539Z" fill="#022E40" />
        <path d="M378 221.134H245.871V227.234H378V221.134Z" fill="#022E40" />
        <path d="M312.709 223.044H117V227.243H312.709V223.044Z" fill="#022E40" />
        <path d="M377.392 217.908H312.202V227.243H377.392V217.908Z" fill="#022E40" />
        <path d="M182.697 231.478H117V239.173H182.697V231.478Z" fill="#022E40" />
        <path d="M378 233.073H245.871V239.173H378V233.073Z" fill="#022E40" />
        <path d="M312.709 234.983H117V239.182H312.709V234.983Z" fill="#022E40" />
        <path d="M377.392 229.838H312.202V239.173H377.392V229.838Z" fill="#022E40" />
        <path d="M182.697 243.219H117V250.914H182.697V243.219Z" fill="#022E40" />
        <path d="M378 244.814H245.871V250.914H378V244.814Z" fill="#022E40" />
        <path d="M312.709 246.724H117V250.923H312.709V246.724Z" fill="#022E40" />
        <path d="M377.392 241.588H312.202V250.923H377.392V241.588Z" fill="#022E40" />
        <path d="M182.697 254.77H117V262.465H182.697V254.77Z" fill="#022E40" />
        <path d="M378 256.356H245.871V262.456H378V256.356Z" fill="#022E40" />
        <path d="M312.709 258.266H117V262.465H312.709V258.266Z" fill="#022E40" />
        <path d="M377.392 253.13H312.202V262.465H377.392V253.13Z" fill="#022E40" />
        <path d="M182.697 266.114H117V273.809H182.697V266.114Z" fill="#022E40" />
        <path d="M378 267.709H245.871V273.809H378V267.709Z" fill="#022E40" />
        <path d="M312.709 269.62H117V273.818H312.709V269.62Z" fill="#022E40" />
        <path d="M377.392 264.484H312.202V273.818H377.392V264.484Z" fill="#022E40" />
        <path d="M182.697 278.251H117V285.946H182.697V278.251Z" fill="#022E40" />
        <path d="M378 278.477H245.871V285.946H378V278.477Z" fill="#022E40" />
        <path d="M312.709 280.973H117V285.946H312.709V280.973Z" fill="#022E40" />
        <path d="M377.392 277.396H312.202V285.946H377.392V277.396Z" fill="#022E40" />
        <path d="M182.697 289.992H117V297.687H182.697V289.992Z" fill="#022E40" />
        <path d="M378 290.217H245.871V297.687H378V290.217Z" fill="#022E40" />
        <path d="M312.709 292.713H117V297.687H312.709V292.713Z" fill="#022E40" />
        <path d="M377.392 289.136H312.202V297.687H377.392V289.136Z" fill="#022E40" />
        <path d="M182.697 302.129H117V309.824H182.697V302.129Z" fill="#022E40" />
        <path d="M378 301.174H245.871V309.824H378V301.174Z" fill="#022E40" />
        <path d="M312.709 304.85H117V309.824H312.709V304.85Z" fill="#022E40" />
        <path d="M182.697 313.474H117V321.168H182.697V313.474Z" fill="#022E40" />
        <path d="M378 312.527H245.871V321.177H378V312.527Z" fill="#022E40" />
        <path d="M312.709 316.195H117V321.168H312.709V316.195Z" fill="#022E40" />
        <path d="M182.697 325.025H117V332.72H182.697V325.025Z" fill="#022E40" />
        <path d="M378 324.07H245.871V332.72H378V324.07Z" fill="#022E40" />
        <path d="M312.709 327.746H117V332.72H312.709V327.746Z" fill="#022E40" />
        <path d="M182.697 336.964H117V344.659H182.697V336.964Z" fill="#022E40" />
        <path d="M378 336.378H245.871V344.659H378V336.378Z" fill="#022E40" />
        <path d="M377.987 335.026H313.773V344.659H377.987V335.026Z" fill="#022E40" />
        <path d="M312.709 339.685H117V344.659H312.709V339.685Z" fill="#022E40" />
        <path d="M182.697 348.704H117V356.399H182.697V348.704Z" fill="#022E40" />
        <path d="M378 348.119H245.871V356.4H378V348.119Z" fill="#022E40" />
        <path d="M377.987 346.767H313.773V356.399H377.987V346.767Z" fill="#022E40" />
        <path d="M312.709 351.426H117V356.399H312.709V351.426Z" fill="#022E40" />
        <path d="M182.697 361.229H117V368.924H182.697V361.229Z" fill="#022E40" />
        <path d="M378 360.643H245.871V368.924H378V360.643Z" fill="#022E40" />
        <path d="M377.987 359.292H313.773V368.924H377.987V359.292Z" fill="#022E40" />
        <path d="M312.709 363.95H117V368.924H312.709V363.95Z" fill="#022E40" />
        <path d="M182.697 372.97H117V380.665H182.697V372.97Z" fill="#022E40" />
        <path d="M378 372.393H245.871V380.674H378V372.393Z" fill="#022E40" />
        <path d="M377.987 371.032H313.773V380.665H377.987V371.032Z" fill="#022E40" />
        <path d="M312.709 375.691H117V380.665H312.709V375.691Z" fill="#022E40" />
        <path d="M182.697 385.296H117V392.991H182.697V385.296Z" fill="#022E40" />
        <path d="M378 384.719H245.871V393H378V384.719Z" fill="#022E40" />
        <path d="M377.987 383.359H313.773V392.991H377.987V383.359Z" fill="#022E40" />
        <path d="M312.709 388.017H117V392.991H312.709V388.017Z" fill="#022E40" />
        <path d="M182.697 158.484H117V167.945H182.697V158.484Z" fill="#022E40" />
        <path d="M312.709 163.107H117V167.936H312.709V163.107Z" fill="#022E40" />
        <path d="M377.392 161.737H312.202V167.936H377.392V161.737Z" fill="#022E40" />
      </g>
      <defs>
        <clipPath id="digitalRainClip">
          <rect width="261" height="331" fill="white" transform="translate(117 62)" />
        </clipPath>
      </defs>
    </svg>
  );
};

